import { memo, useMemo } from 'react'

import { cva } from 'class-variance-authority'
import dayjs from 'dayjs'
import { twMerge } from 'tailwind-merge'

import useDisclosure from 'src/hooks/use-disclosure'

import { getValueOfTailwindVariable } from '../../utils'
import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import { T } from '../Text/Text'

const classes = cva(
  [
    'relative',
    'focus-visible:outline-2',
    'outline-offset-1',
    'outline-outline',
    'border',
    'text-center',
    'flex',
    'items-center',
    'justify-center',
    'gap-2',
    'nowrap',
    'rounded-[6px]',
    'transition-all',
    'duration-100',
    'after:content-[""]',
    'after:rounded-[6px]',
    'after:absolute',
    'after:left-[-1px]',
    'after:top-[-1px]',
    'after:w-[calc(100%_+_2px)]',
    'after:h-[calc(100%_+_2px)]',
    'after:pointer-events-none',
    'after:transition-all',
    'after:duration-50',
  ],
  {
    variants: {
      color: {
        default: [],
        danger: [],
      },
      iconOnly: {
        false: [],
        true: ['leading-4'],
      },
      intent: {
        primary: [],
        default: [],
        text: ['bg-transparent', 'border-transparent'],
      },
      isDisabled: {
        false: ['cursor-pointer', 'active:pt-0.5'],
        true: ['cursor-not-allowed'],
      },
      size: {
        sm: ['h-[24px]'],
        md: ['h-[32px]'],
        lg: ['h-[40px]'],
      },
    },
    compoundVariants: [
      {
        iconOnly: true,
        size: 'sm',
        class: 'w-[24px]',
      },
      {
        iconOnly: false,
        size: 'sm',
        class: 'px-2',
      },
      {
        iconOnly: true,
        size: 'md',
        class: 'w-[32px]',
      },
      {
        iconOnly: false,
        size: 'md',
        class: 'px-4',
      },
      {
        iconOnly: true,
        size: 'lg',
        class: 'w-[40px]',
      },
      {
        iconOnly: false,
        size: 'lg',
        class: 'px-4',
      },
      {
        color: 'default',
        intent: 'primary',
        isDisabled: false,
        class: [
          'bg-neutral-800',
          'border-neutral-800',
          'text-white',
          'hover:bg-neutral-700',
          'after:inset-shadow-[0_-1px_0_0_rgba(0,0,0,0.5)]',
          'active:after:inset-shadow-[0_2px_0_0_rgba(0,0,0,0.5)]',
        ],
      },
      {
        color: 'default',
        intent: 'primary',
        isDisabled: true,
        class: ['bg-neutral-100', 'border-neutral-300', 'text-neutral-400'],
      },
      {
        color: 'danger',
        intent: 'primary',
        isDisabled: false,
        class: [
          'bg-red-600',
          'border-red-600',
          'text-white',
          'hover:bg-red-500',
          'after:inset-shadow-[0_-1px_0_0_rgba(0,0,0,0.25)]',
          'active:after:inset-shadow-[0_2px_0_0_rgba(0,0,0,0.25)]',
        ],
      },
      {
        color: 'danger',
        intent: 'primary',
        isDisabled: true,
        class: ['bg-red-50', 'border-red-300', 'text-red-400'],
      },
      {
        color: 'default',
        intent: 'default',
        isDisabled: false,
        class: [
          'bg-white',
          'border-neutral-200',
          'text-neutral-950',
          'hover:bg-neutral-100',
          'after:inset-shadow-[0_-1px_0_0_rgba(0,0,0,0.25)]',
          'active:after:inset-shadow-[0_2px_0_0_rgba(0,0,0,0.25)]',
        ],
      },
      {
        color: 'default',
        intent: 'default',
        isDisabled: true,
        class: ['bg-white', 'border-neutral-300', 'text-neutral-300'],
      },
      {
        color: 'danger',
        intent: 'default',
        isDisabled: false,
        class: [
          'bg-white',
          'border-red-600',
          'text-red-600',
          'hover:bg-red-50',
          'active:after:inset-shadow-[0_2px_0_0_rgba(231,0,11,0.25)]',
        ],
      },
      {
        color: 'danger',
        intent: 'default',
        isDisabled: true,
        class: ['bg-white', 'border-red-200', 'text-red-300'],
      },
      {
        color: 'default',
        intent: 'text',
        isDisabled: false,
        class: [
          'text-neutral-950',
          'hover:bg-neutral-100',
          'active:after:inset-shadow-[0_2px_0_0_rgba(0,0,0,0.15)]',
        ],
      },
      {
        intent: 'text',
        isDisabled: true,
        class: ['text-neutral-300'],
      },
      {
        color: 'danger',
        intent: 'text',
        isDisabled: false,
        class: [
          'text-red-600',
          'hover:bg-red-50',
          'active:after:inset-shadow-[0_2px_0_0_rgba(231,0,11,0.15)]',
        ],
      },
    ],
    defaultVariants: {
      color: 'default',
      iconOnly: false,
      intent: 'primary',
      isDisabled: false,
      size: 'md',
    },
  }
)

type ButtonColor = 'default' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'
type ButtonVariant = 'primary' | 'default' | 'text'

type ButtonLabelProps =
  | {
      ariaLabel?: string
      children: React.ReactNode
    }
  | {
      ariaLabel: string
      children?: never
    }

type Props = {
  className?: string
  color?: ButtonColor
  icon?: IconKey
  isDisabled?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  size?: ButtonSize
  variant?: ButtonVariant
} & ButtonLabelProps
const Button = ({
  ariaLabel,
  children,
  className,
  color,
  icon,
  isDisabled,
  onClick,
  size,
  variant,
}: Props) => {
  const classNames = twMerge(
    classes({
      color,
      iconOnly: !!icon && !children,
      intent: variant,
      isDisabled,
      size,
    }),
    className
  )

  const TextComponent = size === 'sm' ? T.Body : T.BodyLarge
  const textClassNames = 'text-center text-inherit font-normal'

  /**
   * Remove this code on New Year's Day, 2026
   * Gradually transition from orange to black
   */
  const { isOpen: isHovered, open: hover, close: blur } = useDisclosure()
  const ORANGE_OVERRIDE_STYLES: React.CSSProperties = useMemo(() => {
    if (color === 'default' && variant === 'primary' && !isDisabled) {
      let startColor = getValueOfTailwindVariable('--color-orange-600')
      if (isHovered) {
        startColor = getValueOfTailwindVariable('--color-orange-500')
      }
      const endColor = getValueOfTailwindVariable('--color-neutral-800')
      const countdownStartDays = 37 // November 25th, 2025
      const countdownDaysRemaining = dayjs('01-01-2026').diff(dayjs(), 'days')
      const countdownDaysRemainingPercentage = countdownDaysRemaining / countdownStartDays
      const oklchRegex = new RegExp(/^oklch\((\d+(\.\d+)?)% (\d+(\.\d+)?) (\d+(\.\d+)?)\)$/)
      const startColorMatch = startColor.match(oklchRegex)
      const endColorMatch = endColor.match(oklchRegex)
      if (
        startColorMatch &&
        endColorMatch &&
        countdownDaysRemainingPercentage >= 0 &&
        countdownDaysRemainingPercentage <= 1
      ) {
        const startColorL = parseFloat(startColorMatch[1] ?? '0')
        const startColorC = parseFloat(startColorMatch[3] ?? '0')
        const startColorH = parseFloat(startColorMatch[5] ?? '0')
        const endColorL = parseFloat(endColorMatch[1] ?? '0')
        const endColorC = parseFloat(endColorMatch[3] ?? '0')
        const endColorH = parseFloat(endColorMatch[5] ?? '0')
        const L = endColorL + (startColorL - endColorL) * countdownDaysRemainingPercentage
        const C = endColorC + (startColorC - endColorC) * countdownDaysRemainingPercentage
        const H = endColorH + (startColorH - endColorH) * countdownDaysRemainingPercentage
        const primaryColor = `oklch(${L}% ${C} ${H})`
        return {
          backgroundColor: primaryColor,
          borderColor: primaryColor,
        }
      }
      return {}
    }
    return {}
  }, [color, isDisabled, isHovered, variant])

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={classNames}
      disabled={isDisabled}
      onClick={onClick}
      onMouseEnter={hover}
      onMouseLeave={blur}
      style={ORANGE_OVERRIDE_STYLES}
    >
      {icon && (
        <TextComponent as="span" className={textClassNames}>
          <Icon icon={icon} />
        </TextComponent>
      )}
      {!!children && typeof children === 'string' ? (
        <TextComponent as="span" className={textClassNames}>
          {children}
        </TextComponent>
      ) : (
        children
      )}
    </button>
  )
}

export default memo(Button)
